'use client';

/* @jsxRuntime classic */
// The Roma Vitest/React 17 setup resolves JSX's jsxDEV helper incorrectly for this package.

import React, { useCallback, useContext, useEffect, useRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useOverlayTriggerState } from '@react-stately/overlays';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import {
    OverlayArrow as AriaOverlayArrow,
    OverlayTriggerStateContext,
    Popover as AriaPopover,
    PopoverContext as AriaPopoverContext,
    useContextProps,
} from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { useSurface } from '../surface';
import { useNonModalPopoverDismissal } from './hooks/useNonModalPopoverDismissal';
import { usePopoverFocusRestoration } from './hooks/usePopoverFocusRestoration';
import { RomaPopoverGroupContext, usePopoverGroup } from './hooks/usePopoverGroup';
import { PopoverContext, usePopoverScrollRef } from './hooks/usePopoverScrollRef';
import { PopoverBody, PopoverFooter, PopoverHeader, PopoverSearch } from './PopoverContent';
import { PopoverDialogTrigger, PopoverMenuTrigger } from './PopoverTrigger';
import { styles } from './popoverStyles';

export type PopoverProps = AriaPopoverProps & {
    popoverClassName?: ComponentPropsWithoutRef<'div'>['className'];
    /**
     * Whether to render a pointer arrow on the popover.
     *
     * @default false
     */
    arrow?: boolean;
};
export type PopoverBaseProps = ComponentPropsWithoutRef<'div'>;

const ROMA_POPOVER_DISPLAY_NAME = 'RomaPopover';
const ROMA_POPOVER_BASE_DISPLAY_NAME = 'RomaPopoverBase';

const AriaPopoverWithScrollRef = AriaPopover as React.ForwardRefExoticComponent<
    AriaPopoverProps &
        React.RefAttributes<HTMLElement> & {
            scrollRef?: React.RefObject<Element | null>;
        }
>;

/**
 * Non-functional popover chrome used by the shell to render the panel wrapper.
 */
export const PopoverBase: React.FC<PopoverBaseProps> = ({ className, children, ...props }) => (
    <div {...props} className={cx(ROMA_POPOVER_BASE_DISPLAY_NAME, styles.base, className)}>
        {children}
    </div>
);

PopoverBase.displayName = ROMA_POPOVER_BASE_DISPLAY_NAME;

/**
 * Main Popover component that manages state and context for its subcomponents.
 */
const PopoverShell: React.FC<PopoverProps> = ({ ...props }) => {
    const popoverRef = useRef<Element | null>(null);
    const unregisterPopoverElementRef = useRef<(() => void) | null>(null);
    const overlayTriggerState = useContext(OverlayTriggerStateContext);
    const localOverlayTriggerState = useOverlayTriggerState(props);
    const [contextProps] = useContextProps(props, undefined, AriaPopoverContext);
    const {
        className,
        popoverClassName,
        children,
        triggerRef,
        isOpen,
        defaultOpen,
        onOpenChange,
        placement,
        offset,
        crossOffset,
        containerPadding,
        isNonModal: _contextIsNonModal,
        shouldCloseOnInteractOutside,
        style,
        arrow = false,
        ...restProps
    } = contextProps;
    const isNonModal = props.isNonModal ?? true;
    const surface = useSurface();
    const { popoverContext, scrollRef } = usePopoverScrollRef();
    const { popoverId, popoverGroup, parentPopoverId } = usePopoverGroup();
    const state =
        isOpen !== undefined || defaultOpen !== undefined || !overlayTriggerState
            ? localOverlayTriggerState
            : overlayTriggerState;

    const setPopoverElement = useCallback(
        (element: HTMLElement | null) => {
            unregisterPopoverElementRef.current?.();
            unregisterPopoverElementRef.current = null;
            popoverRef.current = element;

            if (!element) return;

            unregisterPopoverElementRef.current = popoverGroup.registerPopoverElement(
                popoverId,
                parentPopoverId,
                element,
            );
        },
        [parentPopoverId, popoverGroup, popoverId],
    );

    useEffect(() => {
        // Unregister the current DOM node if the popover unmounts before React clears the ref.
        return () => unregisterPopoverElementRef.current?.();
    }, []);

    const containsDescendantPopoverElement = useCallback(
        (target: Node) => popoverGroup.containsDescendantElement(popoverId, target),
        [popoverGroup, popoverId],
    );

    const closePopover = useCallback(() => state.close(), [state]);

    /**
     * Capture runs outermost-first, so one Escape dismisses a whole nested popover stack
     * (a menu and its submenus, or a panel and the menus inside it) rather than peeling a level.
     */
    const dismissStackOnEscape = (event: React.KeyboardEvent): void => {
        if (event.key !== 'Escape' || contextProps.isKeyboardDismissDisabled) return;

        event.stopPropagation();
        closePopover();
    };

    useNonModalPopoverDismissal({
        popoverRef,
        triggerRef,
        isNonModal,
        isOpen: state.isOpen,
        shouldCloseOnInteractOutside,
        containsDescendantElement: containsDescendantPopoverElement,
        close: closePopover,
    });

    usePopoverFocusRestoration({
        triggerRef,
        isOpen,
        defaultOpen,
    });

    return (
        <RomaPopoverGroupContext.Provider value={{ group: popoverGroup, currentPopoverId: popoverId }}>
            <PopoverContext.Provider value={popoverContext}>
                <AriaPopoverWithScrollRef
                    {...restProps}
                    ref={setPopoverElement}
                    triggerRef={triggerRef}
                    isOpen={isOpen}
                    defaultOpen={defaultOpen}
                    onOpenChange={onOpenChange}
                    placement={placement ?? 'bottom'}
                    offset={offset ?? 4}
                    crossOffset={crossOffset ?? 0}
                    containerPadding={containerPadding ?? 24}
                    isNonModal={isNonModal}
                    shouldCloseOnInteractOutside={isNonModal ? () => false : shouldCloseOnInteractOutside}
                    scrollRef={scrollRef}
                    style={(state) => ({
                        ...(typeof style === 'function' ? style(state) : style),
                    })}
                    className={(state) =>
                        cx(
                            ROMA_POPOVER_DISPLAY_NAME,
                            surface,
                            styles.shell,
                            typeof className === 'function' ? className(state) : className,
                        )
                    }
                >
                    {arrow && (
                        <AriaOverlayArrow className="pointer-events-none z-10">
                            <svg
                                viewBox="0 0 100 100"
                                className="size-2.5 fill-bg-primary in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180"
                            >
                                <path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" />
                            </svg>
                        </AriaOverlayArrow>
                    )}
                    <PopoverBase className={popoverClassName} onKeyDownCapture={dismissStackOnEscape}>
                        {children as ReactNode}
                    </PopoverBase>
                </AriaPopoverWithScrollRef>
            </PopoverContext.Provider>
        </RomaPopoverGroupContext.Provider>
    );
};

PopoverShell.displayName = ROMA_POPOVER_DISPLAY_NAME;

export const Popover = PopoverShell as React.FC<PopoverProps> & {
    MenuTrigger: typeof PopoverMenuTrigger;
    DialogTrigger: typeof PopoverDialogTrigger;
    Header: typeof PopoverHeader;
    Body: typeof PopoverBody;
    Footer: typeof PopoverFooter;
    Search: typeof PopoverSearch;
    Base: typeof PopoverBase;
};

Popover.MenuTrigger = PopoverMenuTrigger;
Popover.DialogTrigger = PopoverDialogTrigger;
Popover.Header = PopoverHeader;
Popover.Body = PopoverBody;
Popover.Footer = PopoverFooter;
Popover.Search = PopoverSearch;
Popover.Base = PopoverBase;
